/** @file /p/:id: loads the project document, then shows the header and the dock with every panel. */
import { useCallback, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { api } from '../api/api';
import { browserSyncDeps } from '../api/sync/browserSyncDeps';
import { startLiveSync } from '../api/sync/startLiveSync';
import { PresencePills } from '../panels/activity/PresencePills';
import { ExportButton } from '../panels/export/ExportButton';
import { ExportDialogHost } from '../panels/export/ExportDialogHost';
import { PANELS } from '../panels.gen';
import { browserClock } from '../playback/browserClock';
import { startPlayback } from '../playback/startPlayback';
import { errorText } from '../shell/i18n/errorText';
import { useT } from '../shell/i18n/useT';
import { ShellHeader } from '../shell/ShellHeader';
import { docStore } from '../stores/docStore';
import { toolStore } from '../stores/toolStore';
import { DockLayout } from './DockLayout';
import { loadProject } from './loadProject';

/** The orange Animate-mode frame (spec §9) is the `editor-animate` class; switching modes is Wave 3. */
export function EditorFrame({ projectId }: { projectId: string }) {
  const t = useT();
  const slice = useStore(docStore, (s) => (s.projectId === projectId ? s.slice : null));
  const animate = useStore(toolStore, (s) => s.mode === 'animate');
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const open = useCallback(() => {
    setFailure(null);
    loadProject(api, projectId).catch((thrown: unknown) => { setFailure({ thrown }); });
  }, [projectId]);
  useEffect(open, [open]);
  const loaded = slice !== null;
  // Live sync (WebSocket, optimistic edits, presence, history chords) runs while the document is loaded.
  useEffect(() => (loaded ? startLiveSync(projectId, browserSyncDeps(projectId)) : undefined), [projectId, loaded]);
  // The playback clock (playhead and mix preview) lives as long as the frame, not inside a closable dock panel.
  useEffect(() => startPlayback(browserClock), []);
  // Leaving the editor closes the project: window.__pose2d.dispatch must not keep editing it, and a late doc answer is dropped.
  useEffect(() => () => { docStore.setState(docStore.getInitialState(), true); }, []);
  return (
    <div className={animate ? 'editor-frame editor-animate' : 'editor-frame'} data-testid="editor-frame">
      <ShellHeader t={t} title={slice === null ? projectId : slice.confirmed.meta.name} actions={slice === null ? undefined : <ExportButton t={t} />} />
      {slice === null ? null : <PresencePills />}
      {failure === null ? null : (
        <div role="alert" className="shell-error">
          <p>{errorText(t, failure.thrown)}</p>
          <button type="button" onClick={open}>{t('shell.retry')}</button>
        </div>
      )}
      {slice === null && failure === null ? <p className="shell-splash">{t('shell.loading')}</p> : null}
      {slice === null ? null : <DockLayout panels={PANELS} />}
      {slice === null ? null : <ExportDialogHost projectId={projectId} />}
    </div>
  );
}
